import { SkeletonText, useToast } from '@chakra-ui/react';
import React, { useCallback, useState } from 'react';

import CaliforniaSharingConsent from '../components/CaliforniaSharingConsent';
import DynamicLinkContent from '../components/DynamicLinkContent';
import Modal from '../components/Modal';
import { getTempInfoCountryConfig } from '../constants/countries';
import { LDFlags } from '../constants/flags';
import { DEFAULT_INITIAL_SCORE, DEFAULT_LOAN_TERM } from '../constants/paymentCalculator';
import { GLBA_PRIVACY_NOTICE } from '../constants/urls';
import {
  ConsentAgreementTypeEnum,
  ConsentNameEnumType,
  PayoffRequestStatus,
  StateAbbreviation,
  TemporaryData,
  TtGetFeesSourceType,
  useCurrentConsentAgreementTextQuery,
  useExecuteExternalSoftPullMutation,
  useExternalUserPayoffRequestLazyQuery,
  useSaveConsentMutation,
} from '../gql/generated/graphql';
import { RudderEvent, rudderanalytics } from '../services/analytics/rudderstack';
import { logger } from '../services/sentry';
import { getCreditScore } from '../utils/creditScore';
import { createErrorToast } from '../utils/toast';
import { usePayoffIntelligence } from './useAutoFetchPayoff/usePayoffIntelligence';
import useFlag from './useFlag';

const openPrivacyNotice = () => window.open(GLBA_PRIVACY_NOTICE, '_blank', 'noopener,noreferrer');

const disclosureTextProps = {
  textAlign: 'left',
  color: 'gray',
  fontSize: '12px',
  handlerProps: { fontSize: '12px' },
} as const;

interface UsePrequalificationOptions {
  skip?: boolean;
  state?: TemporaryData['state'];
}

interface UsePrequalificationReturn {
  shouldExecutePrequalification: boolean;
  isSubmitStep: boolean;
  consentLoading: boolean;
  termsLoading: boolean;
  consentAgreementText?: string;
  termsCopyText?: string;
  executePrequalification: (tempInfoData?: TemporaryData) => Promise<boolean | null>;
  savePrivacyConsents: () => Promise<boolean>;
  prequalLoading: boolean;
  termsModalOpen: boolean;
  openTermsModal: () => void;
  closeTermsModal: () => void;
  TermsAndConditionsModal: React.ReactNode;
  ConsentContent: React.ReactNode;
}

export const usePrequalification = (
  options: UsePrequalificationOptions = {},
): UsePrequalificationReturn => {
  const { skip, state } = options;
  const toast = useToast();
  const [termsModalOpen, setTermsModalOpen] = useState(false);
  const [californiaSharingConsented, setCaliforniaSharingConsented] = useState(false);

  const shouldExecutePrequalification = !skip;
  const showPrivacyNotice = !!useFlag(LDFlags.GLBA_PRIVACY_NOTICE);
  const showCaliforniaSharingConsent =
    !!useFlag(LDFlags.CALIFORNIA_DATA_SHARING_CONSENT) && state === StateAbbreviation.Ca;

  const { data: consentAgreement, loading: consentLoading } = useCurrentConsentAgreementTextQuery({
    variables: {
      agreementType: ConsentAgreementTypeEnum.HardPull,
      version: 3,
    },
    skip: !shouldExecutePrequalification,
  });

  const { data: termsCopy, loading: termsLoading } = useCurrentConsentAgreementTextQuery({
    variables: {
      agreementType: ConsentAgreementTypeEnum.TermsAndConditions,
      version: showPrivacyNotice ? 3 : 2,
    },
    skip: !shouldExecutePrequalification,
  });

  const { data: privacyNotice, loading: privacyNoticeLoading } =
    useCurrentConsentAgreementTextQuery({
      variables: { agreementType: ConsentAgreementTypeEnum.GlbaPrivacyNotice },
      skip: !showPrivacyNotice,
    });

  const { data: californiaSharingConsentCopy, loading: californiaSharingConsentLoading } =
    useCurrentConsentAgreementTextQuery({
      variables: { agreementType: ConsentAgreementTypeEnum.CaliforniaDataSharingConsent },
      skip: !showCaliforniaSharingConsent,
    });

  const [executeSoftPullPrequalification, { loading: prequalLoading }] =
    useExecuteExternalSoftPullMutation({
      context: {
        isErrorHandled: true,
      },
    });

  const [saveConsentMutation] = useSaveConsentMutation({
    context: {
      isErrorHandled: true,
    },
  });

  const [fetchPayoffRequest] = useExternalUserPayoffRequestLazyQuery({
    fetchPolicy: 'network-only',
  });

  const { checkLienholderSupportedForPI } = usePayoffIntelligence();

  const privacyNoticeText = privacyNotice?.currentConsentAgreementText?.text;
  const californiaSharingConsentText =
    californiaSharingConsentCopy?.currentConsentAgreementText?.text;

  const savePrivacyConsents = useCallback(async (): Promise<boolean> => {
    if (showPrivacyNotice && !privacyNoticeText) {
      logger.error('usePrequalification.tsx', 'GLBA privacy notice copy is missing', '', null);
      toast(createErrorToast({ errorMessage: 'Failed to load the privacy notice' }));
      return false;
    }

    const consents = [
      ...(showPrivacyNotice && privacyNoticeText
        ? [
            {
              type: ConsentNameEnumType.GlbaPrivacyNotice,
              version: privacyNotice?.currentConsentAgreementText?.version,
            },
          ]
        : []),
      ...(showCaliforniaSharingConsent && californiaSharingConsented
        ? [
            {
              type: ConsentNameEnumType.CaliforniaDataSharingConsent,
              version: californiaSharingConsentCopy?.currentConsentAgreementText?.version,
            },
          ]
        : []),
    ];

    try {
      const results = await Promise.all(
        consents.map((variables) => saveConsentMutation({ variables })),
      );
      return results.every((result) => !!result?.data?.saveConsent?.consent?.id);
    } catch (error) {
      logger.error(
        'usePrequalification.tsx',
        'There was an error when saving the privacy consents',
        '',
        error,
      );
      toast(createErrorToast({ errorMessage: 'Failed to save the privacy consents' }));
      return false;
    }
  }, [
    toast,
    saveConsentMutation,
    showPrivacyNotice,
    privacyNoticeText,
    showCaliforniaSharingConsent,
    californiaSharingConsented,
    privacyNotice?.currentConsentAgreementText?.version,
    californiaSharingConsentCopy?.currentConsentAgreementText?.version,
  ]);

  const saveConsents = useCallback(async (): Promise<boolean> => {
    const [hardPullResult, termsResult] = await Promise.all([
      saveConsentMutation({
        variables: {
          type: ConsentNameEnumType.CreditScoreHardPull,
        },
      }),
      saveConsentMutation({
        variables: {
          type: ConsentNameEnumType.TermsAndConditions,
          version: termsCopy?.currentConsentAgreementText?.version,
        },
      }),
    ]);

    const consent = hardPullResult?.data?.saveConsent?.consent;
    const termsConsent = termsResult?.data?.saveConsent?.consent;

    const missing = !consent?.id
      ? 'credit pull'
      : !termsConsent?.id
      ? 'terms and conditions'
      : null;

    if (missing) {
      logger.error(
        'usePrequalification.tsx',
        `There was an error when saving the ${missing} consent`,
        '',
        null,
      );

      return false;
    }

    return true;
  }, [saveConsentMutation, termsCopy?.currentConsentAgreementText?.version]);

  const executePrequalification = useCallback(
    async (tempInfoData: TemporaryData): Promise<boolean | null> => {
      if (
        !shouldExecutePrequalification ||
        !tempInfoData ||
        !consentAgreement?.currentConsentAgreementText?.version
      ) {
        return null;
      }

      const success = await saveConsents();
      if (!success || !getTempInfoCountryConfig(tempInfoData).creditPull) {
        return null;
      }

      // PI already in flight. API runs prequal on PI callback.
      const { data: payoffRequestData } = await fetchPayoffRequest();
      const hasPayoffActiveRequest =
        payoffRequestData?.externalUserPayoffRequest &&
        payoffRequestData?.externalUserPayoffRequest?.status !== PayoffRequestStatus.Completed &&
        payoffRequestData?.externalUserPayoffRequest?.status !== PayoffRequestStatus.Cancelled;

      if (hasPayoffActiveRequest) {
        return null;
      }

      // PI-supported lienholder: useNextSteps fires PI post-flow;
      // API runs prequal via handlePayoffIntelligenceCallback -> processInProgressPayoffRequestSuccess -> runCOMPrequalification
      // after PI callback completed
      try {
        const { isSupported } = await checkLienholderSupportedForPI();
        if (isSupported) {
          return null;
        }
      } catch (error) {
        logger.error(
          'usePrequalification.tsx',
          'PI support check failed; treating as unsupported',
          '',
          error,
        );
      }

      // PI-unsupported AND no payoff: deal routes to ImportType.PiUnavailable.
      if (!tempInfoData.vehicle_payoff) {
        return null;
      }

      try {
        const prequal = await executeSoftPullPrequalification({
          variables: {
            paymentEstimateInput: {
              term: DEFAULT_LOAN_TERM,
              payoff: tempInfoData.vehicle_payoff ?? 0,
              moneyDown: tempInfoData.down_payment ?? 0,
              creditScore: getCreditScore(DEFAULT_INITIAL_SCORE),
              zipCode: tempInfoData.zip as string,
              vin: tempInfoData.vin,
              year: tempInfoData.year,
              make: tempInfoData.make,
              model: tempInfoData.model,
              vehicleType: tempInfoData.vehicle_type,
              fuelType: tempInfoData.fuel_type,
              ttGetFeesSource: TtGetFeesSourceType.ComPrequalification,
            },
            isHardPull: true,
          },
        });

        const passedPrequal = prequal?.data?.executeExternalSoftPull;

        rudderanalytics.track(RudderEvent.CreditSoftPull, {
          prequalification_success: passedPrequal,
        });

        return passedPrequal ?? null;
      } catch (error) {
        logger.error(
          'usePrequalification.tsx',
          'There was an error when running the prequalification',
          '',
          error,
        );
        return null;
      }
    },
    [
      shouldExecutePrequalification,
      consentAgreement?.currentConsentAgreementText?.version,
      executeSoftPullPrequalification,
      saveConsents,
      fetchPayoffRequest,
      checkLienholderSupportedForPI,
    ],
  );

  const openTermsModal = useCallback(() => {
    setTermsModalOpen(true);
  }, []);

  const closeTermsModal = useCallback(() => {
    setTermsModalOpen(false);
  }, []);

  const TermsAndConditionsModal = shouldExecutePrequalification ? (
    <Modal title="Terms &amp; Conditions" isOpen={termsModalOpen} onClose={closeTermsModal}>
      <DynamicLinkContent
        content={termsCopy?.currentConsentAgreementText?.text || ''}
        dynamicContentHandlers={[
          {
            content: 'Privacy Notice',
            onClick: openPrivacyNotice,
            trackClick: 'open_glba_privacy_notice_from_terms',
          },
        ]}
      />
    </Modal>
  ) : null;

  const ConsentContent = (
    <>
      {showCaliforniaSharingConsent && californiaSharingConsentText && (
        <CaliforniaSharingConsent
          text={californiaSharingConsentText}
          checked={californiaSharingConsented}
          onChange={setCaliforniaSharingConsented}
        />
      )}
      {shouldExecutePrequalification && consentLoading && <SkeletonText minW="85%" noOfLines={4} />}
      {shouldExecutePrequalification && !consentLoading && (
        <DynamicLinkContent
          content={consentAgreement?.currentConsentAgreementText?.text || ''}
          dynamicContentHandlers={[
            {
              content: 'here',
              onClick: openTermsModal,
              trackClick: 'open_terms_and_conditions',
            },
          ]}
          {...disclosureTextProps}
        />
      )}
      {showPrivacyNotice && privacyNoticeText && (
        <DynamicLinkContent
          content={privacyNoticeText}
          dynamicContentHandlers={[
            {
              content: 'View Privacy Notice',
              onClick: openPrivacyNotice,
              trackClick: 'open_glba_privacy_notice',
            },
          ]}
          {...disclosureTextProps}
        />
      )}
    </>
  );

  return {
    shouldExecutePrequalification,
    isSubmitStep:
      shouldExecutePrequalification || showPrivacyNotice || showCaliforniaSharingConsent,
    consentLoading: consentLoading || privacyNoticeLoading || californiaSharingConsentLoading,
    termsLoading,
    consentAgreementText: consentAgreement?.currentConsentAgreementText?.text || undefined,
    termsCopyText: termsCopy?.currentConsentAgreementText?.text || undefined,
    executePrequalification: (tempInfoData?: TemporaryData) =>
      executePrequalification(tempInfoData as TemporaryData),
    savePrivacyConsents,
    prequalLoading,
    termsModalOpen,
    openTermsModal,
    closeTermsModal,
    TermsAndConditionsModal,
    ConsentContent,
  };
};
